/*
 * gclid_capture.js -- Marbily storefront click-id capture.
 *
 * WHY THIS EXISTS
 *   Shopify does not persist the Google click id. A sweep of 482 orders over
 *   60 days (2026-08-25) found zero carrying a gclid: Shopify keeps utm_* in
 *   customerJourneySummary but drops every other query parameter, and the
 *   stored landingPageUrl is normalised clean. Without a click id there is
 *   nothing for Google to match an offline conversion to.
 *
 * WHAT IT DOES
 *   1. Reads gclid / gbraid / wbraid from the landing URL.
 *   2. Stores it for 90 days -- the account's click-through lookback window,
 *      so a stored id stays matchable for exactly as long as Google will
 *      accept it. An id older than 90 days is dropped rather than uploaded.
 *   3. Writes it onto the cart as an attribute, so it lands on the order as
 *      a customAttribute. This is the same mechanism TagFly already uses for
 *      tf_client_id, which is present on 309 of 482 recent orders -- proof
 *      the pattern works on this store.
 *   4. Captures the visitor's Shopify tracking-consent state next to it.
 *      Poland is in the EEA, so Google requires ad_user_data /
 *      ad_personalization consent signals on uploaded conversions.
 *
 * INSTALLATION -- see RUNBOOK.md step 1. Not installed by this project.
 *
 * DELIBERATE NON-GOALS
 *   No PII is read or stored. No network call is made unless a click id is
 *   actually present AND the cart does not already carry it.
 */
(function () {
  "use strict";

  var KEY = "marbily_click_id";
  var TTL_DAYS = 90;                 // matches conversion_action lookback
  var TTL_MS = TTL_DAYS * 24 * 60 * 60 * 1000;
  var PARAMS = ["gclid", "gbraid", "wbraid"];

  function now() { return new Date().getTime(); }

  function readStore() {
    try {
      var raw = window.localStorage.getItem(KEY);
      if (!raw) { return null; }
      var rec = JSON.parse(raw);
      if (!rec || !rec.value || !rec.ts) { return null; }
      if (now() - rec.ts > TTL_MS) {          // past the lookback window
        window.localStorage.removeItem(KEY);
        return null;
      }
      return rec;
    } catch (e) { return null; }             // private mode, quota, bad JSON
  }

  function writeStore(rec) {
    try { window.localStorage.setItem(KEY, JSON.stringify(rec)); } catch (e) {}
    // Cookie mirror: survives localStorage being unavailable, and is readable
    // by a server-side tag if one is ever added. SameSite=Lax so it survives
    // the top-level navigation into checkout.
    try {
      document.cookie = KEY + "=" + encodeURIComponent(rec.type + ":" + rec.value) +
        ";path=/;max-age=" + Math.floor(TTL_MS / 1000) + ";SameSite=Lax;Secure";
    } catch (e) {}
  }

  function fromUrl() {
    var q;
    try { q = new URLSearchParams(window.location.search); } catch (e) { return null; }
    for (var i = 0; i < PARAMS.length; i++) {
      var v = q.get(PARAMS[i]);
      // Click ids are opaque tokens. Bound the length and charset so a junk or
      // hostile query string can never be written onto an order.
      if (v && /^[A-Za-z0-9_.\-]{10,512}$/.test(v)) {
        return { type: PARAMS[i], value: v, ts: now() };
      }
    }
    return null;
  }

  function consentState() {
    // Shopify Customer Privacy API. Absent on some themes -- then we record
    // "unknown" and the uploader sends UNSPECIFIED rather than guessing.
    try {
      var p = window.Shopify && window.Shopify.customerPrivacy;
      if (!p || typeof p.marketingAllowed !== "function") { return "unknown"; }
      return (p.marketingAllowed() ? "ad_user_data=granted" : "ad_user_data=denied") +
             ";" + (p.saleOfDataAllowed && p.saleOfDataAllowed()
                     ? "ad_personalization=granted" : "ad_personalization=denied");
    } catch (e) { return "unknown"; }
  }

  function syncCart(rec) {
    // Only write when the cart does not already carry this exact value --
    // keeps this to at most one POST per visitor per click id.
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        var consent = consentState();
        if (attrs[rec.type] === rec.value && attrs.marbily_consent === consent) { return; }
        var body = {};
        body[rec.type] = rec.value;
        body.marbily_click_ts = String(rec.ts);      // click time, for freshness checks
        body.marbily_consent = consent;
        wrotePage = true;
        return fetch("/cart/update.js", {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: body })
        });
      })
      .catch(function () { /* never break checkout over analytics */ });
  }

  function consentDenied() {
    // Explicit decline on the cookie banner means DECLINE: we do not store, and we
    // purge what exists. Only an unambiguous "no" from Shopify's Customer Privacy
    // API counts -- an absent/late API is "unknown", handled by labelling instead.
    try {
      var p = window.Shopify && window.Shopify.customerPrivacy;
      return !!(p && typeof p.marketingAllowed === "function" && p.marketingAllowed() === false);
    } catch (e) { return false; }
  }

  function purge() {
    try { window.localStorage.removeItem(KEY); } catch (e) {}
    try { document.cookie = KEY + "=;path=/;max-age=0;SameSite=Lax;Secure"; } catch (e) {}
    fetch("/cart.js", { credentials: "same-origin" })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var attrs = (cart && cart.attributes) || {};
        var dirty = false, body = {};
        for (var i = 0; i < PARAMS.length; i++) {
          if (attrs[PARAMS[i]] || wrotePage) { body[PARAMS[i]] = ""; dirty = true; }
        }
        if (attrs.marbily_click_ts || wrotePage) { body.marbily_click_ts = ""; dirty = true; }
        if (!dirty) { return; }
        wrotePage = false;
        body.marbily_consent = consentState();
        return fetch("/cart/update.js", {
          method: "POST", credentials: "same-origin",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ attributes: body })
        });
      })
      .catch(function () { /* never break checkout over analytics */ });
  }

  var pending = null;                    // in-memory only, so a same-page accept can still capture
  var wrotePage = false;                 // this pageview posted attrs -> a later purge must clear them
                                         // even if its cart read races ahead of that write

  function privacy() {
    try {
      var p = window.Shopify && window.Shopify.customerPrivacy;
      return (p && typeof p.marketingAllowed === "function") ? p : null;
    } catch (e) { return null; }
  }

  function run() {
    if (consentDenied()) {               // decline (or strict-mode pre-answer) -> nothing persisted
      pending = pending || fromUrl();
      purge();
      return;
    }
    var found = pending || fromUrl();
    pending = null;
    if (found) {
      writeStore(found);                 // a fresh paid click always supersedes an older one
    }
    var rec = found || readStore();
    if (rec) {
      syncCart(rec);
    }
  }

  run();
  // Shopify's Customer Privacy API usually loads AFTER this snippet. Re-check briefly so a
  // strict-mode "not allowed" purges the eager capture within seconds instead of never.
  if (!privacy()) {
    var tries = 0, ms = window.__MARBILY_CONSENT_POLL_MS || 300;
    var tick = function () {
      if (privacy()) { run(); return; }
      if (++tries < 20) { setTimeout(tick, ms); }
    };
    try { setTimeout(tick, ms); } catch (e) {}
  }
  // And the banner is answered after load -- accept starts capture, decline purges.
  try { document.addEventListener("visitorConsentCollected", run); } catch (e) {}
})();
